import { useState, FormEvent } from 'react';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface WeightFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

/**
 * WeightForm Component
 *
 * Form for logging weight entries with decimal kg values.
 * Posts to /api/weight endpoint.
 */
export function WeightForm({ selectedDate, onSuccess }: WeightFormProps) {
	const [weight, setWeight] = useState('');
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingBlobs, setPendingBlobs] = useState<Blob[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0);

	const resetForm = () => {
		setWeight('');
		setNotes('');
		setPendingBlobs([]);
		setUploaderKey((k) => k + 1);
	};

	const uploadImages = async (entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			const result = await convertToAVIF(blob);

			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', 'weight');
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());

			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate weight
		const weightValue = parseFloat(weight);
		if (isNaN(weightValue) || weightValue <= 0) {
			setError('Berat harus berupa angka positif');
			setIsSubmitting(false);
			return;
		}

		try {
			const res = await fetch(apiUrl('/api/weight'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					weight: weight,
					notes: notes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (pendingBlobs.length > 0) {
				await uploadImages(data.id, pendingBlobs);
			}

			onSuccess?.('Berat badan berhasil dicatat!');
			resetForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Berat Badan</h2>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			{/* Weight Input */}
			<div>
				<label
					htmlFor="weight"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Berat (kg) <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					inputMode="decimal"
					id="weight"
					value={weight}
					onChange={(e) => setWeight(e.target.value)}
					placeholder="Contoh: 75.55"
					required
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
				<p className="mt-1 text-xs text-gray-500">
					Masukkan berat dalam kilogram (bisa desimal, contoh: 75.55)
				</p>
			</div>

			{/* Notes Input */}
			<div>
				<label
					htmlFor="weight-notes"
					className="block text-sm font-medium text-gray-700 mb-2"
				>
					Catatan (opsional)
				</label>
				<textarea
					id="weight-notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={3}
					placeholder="Catatan tambahan..."
					disabled={isSubmitting}
					className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
				/>
			</div>

			{/* Image Upload Section (inline) */}
			<div>
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Foto (opsional)
				</label>
				<ImageUploader
					key={uploaderKey}
					entryType="weight"
					onPendingBlobsChange={setPendingBlobs}
					disabled={isSubmitting}
				/>
			</div>

			{/* Submit Button - Sticky on mobile for easy access */}
			<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
				<button
					type="submit"
					disabled={isSubmitting}
					className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
				>
					{isSubmitting ? 'Menyimpan...' : 'Simpan'}
				</button>
			</div>
		</form>
	);
}
